// src/App.js
import React, { useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { onAuthChange, adminLogout, checkIsAdmin } from './services/firebase';
import { FEATURES } from './config/features';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Riders from './pages/Riders';
import Rides from './pages/Rides';
import Shops from './pages/Shops';
import Users from './pages/Users';
import Orders from './pages/Orders';
import Merchants from './pages/Merchants';
import Analytics from './pages/Analytics';
import Payouts from './pages/Payouts';
import RiderCollections from './pages/RiderCollections';
import Promos from './pages/Promos';
import Settings from './pages/Settings';
import ActivityLog from './pages/ActivityLog';

const NAV = [
  { section: 'OVERVIEW' },
  { id: 'dashboard', label: 'Dashboard', icon: '⚡' },
  ...(FEATURES.RIDES_ENABLED ? [
    { section: 'RIDES' },
    { id: 'rides', label: 'All Rides', icon: '🛺' },
  ] : []),
  { section: FEATURES.RIDES_ENABLED ? 'RIDERS' : 'DELIVERY' },
  { id: 'riders', label: FEATURES.RIDES_ENABLED ? 'Riders' : 'Delivery Partners', icon: '👨‍✈️' },
  { section: 'GROCERY' },
  { id: 'shops', label: 'Shops & Products', icon: '🏪' },
  { id: 'merchants', label: 'Merchants', icon: '🤝' },
  { id: 'orders', label: 'Orders', icon: '📦' },
  { section: 'USERS' },
  { id: 'users', label: 'Customers', icon: '👥' },
  { section: 'INSIGHTS' },
  { id: 'analytics', label: 'Analytics', icon: '📊' },
  { section: 'FINANCE' },
  { id: 'rider-collections', label: 'Rider Collections', icon: '💵' },
  { id: 'payouts', label: 'Payouts', icon: '💰' },
  { id: 'promos', label: 'Promos', icon: '🏷️' },
  { section: 'SYSTEM' },
  { id: 'settings', label: 'Settings', icon: '⚙️' },
  { id: 'activity', label: 'Activity Log', icon: '📋' },
];

export default function App() {
  const [user, setUser] = useState(undefined);
  const [isAdmin, setIsAdmin] = useState(undefined);
  const [page, setPage] = useState('dashboard');

  useEffect(() => {
    return onAuthChange(async (authUser) => {
      setUser(authUser);
      if (authUser) {
        setIsAdmin(await checkIsAdmin(authUser.uid));
      } else {
        setIsAdmin(undefined);
      }
    });
  }, []);

  if (user === undefined || (user && isAdmin === undefined)) return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🏘️</div>
        <div className="spinner" style={{ margin: '0 auto' }} />
      </div>
    </div>
  );

  if (!user) return (
    <>
      <Login />
      <Toaster position="top-right" toastOptions={{ style: { background: '#141a22', color: '#e2e8f0', border: '1px solid #1e2730', borderRadius: 10 } }} />
    </>
  );

  if (!isAdmin) return (
  <>
    <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ maxWidth: 440, background: 'var(--card)', border: '1px solid var(--border2)', borderRadius: 16, padding: 28 }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
        <div style={{ fontFamily: 'Clash Display, sans-serif', fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Not an admin account</div>
        <p style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 16 }}>
          You signed in as <strong>{user.email}</strong>, but this login is not registered as a MyTownly admin in Firestore.
        </p>
        <p style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, marginBottom: 8 }}>
          To approve riders like <strong>Abraz</strong>, add your UID in Firebase Console:
        </p>
        <ol style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.7, paddingLeft: 20, marginBottom: 16 }}>
          <li>Authentication → copy your User UID</li>
          <li>Firestore → collection <code style={{ color: 'var(--green)' }}>admin</code> → Add document → Document ID = that UID</li>
          <li>Refresh this page or sign in again</li>
        </ol>
        <div style={{ fontFamily: 'JetBrains Mono', fontSize: 11, color: 'var(--green)', wordBreak: 'break-all', padding: 12, background: 'var(--green-dim)', borderRadius: 8, marginBottom: 16 }}>
          {user.uid}
        </div>
        <button type="button" className="btn btn-ghost" style={{ width: '100%' }} onClick={() => adminLogout()}>Sign out</button>
      </div>
    </div>
    <Toaster position="top-right" toastOptions={{ style: { background: '#141a22', color: '#e2e8f0', border: '1px solid #1e2730', borderRadius: 10 } }} />
  </>
  );

  const pages = {
    dashboard: <Dashboard />,
    ...(FEATURES.RIDES_ENABLED ? { rides: <Rides /> } : {}),
    riders: <Riders />,
    shops: <Shops />,
    merchants: <Merchants />,
    users: <Users />,
    orders: <Orders />,
    analytics: <Analytics />,
    payouts: <Payouts />,
    'rider-collections': <RiderCollections />,
    promos: <Promos />,
    settings: <Settings />,
    activity: <ActivityLog />,
  };

  return (
    <>
      <div className="admin-layout">
        <aside className="sidebar">
          <div className="sidebar-logo">
            My<span>Townly</span>
            <sub>ADMIN CONSOLE</sub>
          </div>

          <nav style={{ padding: '8px 0', flex: 1 }}>
            {NAV.map((item, i) => {
              if (item.section) return <div key={i} className="nav-section">{item.section}</div>;
              return (
                <button key={item.id} className={`nav-item ${page === item.id ? 'active' : ''}`} onClick={() => setPage(item.id)}>
                  <span className="icon">{item.icon}</span>
                  {item.label}
                </button>
              );
            })}
          </nav>

          <div style={{ padding: 12, borderTop: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', marginBottom: 8 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--green-dim)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, border: '1px solid var(--green-mid)', flexShrink: 0 }}>👑</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Admin</div>
                <div style={{ fontSize: 10, color: 'var(--text2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'JetBrains Mono' }}>{user.email}</div>
              </div>
            </div>
            <button className="nav-item" onClick={adminLogout} style={{ color: 'var(--red)', width: '100%' }}>
              <span className="icon">🚪</span> Sign Out
            </button>
          </div>
        </aside>

        <main className="main">
          <div className="topbar">
            <div className="topbar-title">
              {NAV.find(n => n.id === page)?.icon} {NAV.find(n => n.id === page)?.label}
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--green)' }}>
              <span className="live-dot" />
              Live · Grocery{FEATURES.RIDES_ENABLED ? ' + Rides' : ''}
            </div>
          </div>
          {pages[page]}
        </main>
      </div>

      <Toaster position="top-right" toastOptions={{
        style: { background: '#141a22', color: '#e2e8f0', border: '1px solid #1e2730', borderRadius: 10, fontFamily: 'Epilogue, sans-serif' },
        success: { iconTheme: { primary: '#00e676', secondary: '#080c10' } },
      }} />
    </>
  );
}
